/**
 * Project-wide HTML sanitiser. Every `dangerouslySetInnerHTML` payload
 * must flow through this helper.
 *
 *   - `safeArticleHTML` — wide allowlist for editorial article bodies
 *     (paragraphs, headings, images, links, lists, blockquotes, tables).
 *   - `safeTemplateHTML` — stricter allowlist for admin-controlled email
 *     and invoice templates. Permits inline styles for layout, refuses
 *     anything that can execute JS or load remote assets beyond images.
 *   - `safeMarkdownHTML` — minimal allowlist for AI-derived inline
 *     markdown (strong, em, code, br).
 *
 * All variants strip `<script>`, event-handler attributes (`onclick`,
 * `onerror`, …) and javascript: / vbscript: / data: URIs by default.
 *
 * Implementation note: `isomorphic-dompurify` ships both the browser
 * and SSR versions in one entry. The bundle cost is ~50 KB gzipped,
 * mostly DOMPurify itself. We accept that trade-off because every
 * untrusted HTML source on this app rounds through here.
 */

import DOMPurify from "isomorphic-dompurify";

const ARTICLE_TAGS = [
  "a",
  "abbr",
  "article",
  "b",
  "blockquote",
  "br",
  "caption",
  "cite",
  "code",
  "dd",
  "del",
  "details",
  "div",
  "dl",
  "dt",
  "em",
  "figcaption",
  "figure",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "hr",
  "i",
  "img",
  "ins",
  "kbd",
  "li",
  "mark",
  "ol",
  "p",
  "picture",
  "pre",
  "q",
  "s",
  "samp",
  "section",
  "small",
  "source",
  "span",
  "strong",
  "sub",
  "summary",
  "sup",
  "table",
  "tbody",
  "td",
  "tfoot",
  "th",
  "thead",
  "time",
  "tr",
  "u",
  "ul",
];

const ARTICLE_ATTRS = [
  "alt",
  "cite",
  "class",
  "datetime",
  "dir",
  "href",
  "id",
  "lang",
  "loading",
  "media",
  "rel",
  "sizes",
  "src",
  "srcset",
  "target",
  "title",
  "type",
  "width",
  "height",
  "scope",
  "colspan",
  "rowspan",
];

const TEMPLATE_TAGS = [
  "a",
  "b",
  "br",
  "div",
  "em",
  "h1",
  "h2",
  "h3",
  "h4",
  "hr",
  "i",
  "img",
  "li",
  "ol",
  "p",
  "small",
  "span",
  "strong",
  "table",
  "tbody",
  "td",
  "tfoot",
  "th",
  "thead",
  "tr",
  "u",
  "ul",
];

const TEMPLATE_ATTRS = [
  "alt",
  "class",
  "colspan",
  "height",
  "href",
  "id",
  "src",
  "style", // inline styles permitted for layout, but DOMPurify still sanitises url()
  "target",
  "title",
  "width",
];

const MARKDOWN_TAGS = ["a", "br", "code", "em", "i", "li", "ol", "p", "pre", "strong", "ul"];
const MARKDOWN_ATTRS = ["href", "target", "rel"];

// DOMPurify gotcha: `USE_PROFILES.html` UNIONS its tag allowlist with
// whatever you pass in `ALLOWED_TAGS`, defeating the narrow markdown
// profile below. We rely on explicit `ALLOWED_TAGS` per variant and
// keep COMMON to FORBID_* + ALLOW_DATA_ATTR only.
const COMMON = {
  ALLOW_DATA_ATTR: false,
  FORBID_TAGS: ["style", "script", "iframe", "object", "embed", "form", "input", "button"],
  FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus", "formaction"],
};

export function safeArticleHTML(input: string | null | undefined): string {
  return DOMPurify.sanitize(input ?? "", {
    ...COMMON,
    ALLOWED_TAGS: ARTICLE_TAGS,
    ALLOWED_ATTR: ARTICLE_ATTRS,
  });
}

export function safeTemplateHTML(input: string | null | undefined): string {
  return DOMPurify.sanitize(input ?? "", {
    ...COMMON,
    ALLOWED_TAGS: TEMPLATE_TAGS,
    ALLOWED_ATTR: TEMPLATE_ATTRS,
  });
}

export function safeMarkdownHTML(input: string | null | undefined): string {
  return DOMPurify.sanitize(input ?? "", {
    ...COMMON,
    ALLOWED_TAGS: MARKDOWN_TAGS,
    ALLOWED_ATTR: MARKDOWN_ATTRS,
  });
}
